import React, { useEffect, useMemo, useRef } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isPercentageComplete,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  isValidPositiveNumber,
  queryIsLoading,
  useGetDairyFeedTypes,
  useGetFootprintingDairyFeedInformationByPrimaryDataId,
  useGetFootprintingDairyHerdSize,
  useGetFootprintingDairyPrimaryDataById,
  useKeyedFormList,
  useMutateFootprintingDairyFeedInformation,
  useSaveFormOnUnmount,
  useTranslation,
} from "utils";

import { DEFAULT_DRY_MATTER_PERCENTAGE, KG_PER_TONNE, getUnitDisplayLabel } from "consts";

import { DAIRY_FEED_ENERGY_UNIT, DairyFeedType, ENUM_DAIRY_FEED_ENERGY_UNIT, UploadableFeedInformation } from "models";

import {
  AlertBanner,
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputImageCard,
  InputNumber,
  InputNumberWithUnitSelector,
  Option,
  PageSpin,
  ProgressBarCardWithContent,
  Select,
  Switch,
  Tag,
  Typography,
} from "components";

interface DairyFeedTypesFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const STANDARD_FEED_ENERGY_CONTENT_MJNEL = 7;
const CORRECTED_ENERGY_CONSTANT = KG_PER_TONNE / STANDARD_FEED_ENERGY_CONTENT_MJNEL;

const createFeedTypePlaceholder = (): UploadableFeedInformation => ({
  id: undefined,
  feedTypeId: "",
  isSelfProduced: null,
  totalAmountFedTs: null,
  silagePct: null,
  energyValue: null,
  energyUnit: DAIRY_FEED_ENERGY_UNIT.MJNEL,
  dryMatterPct: null,
  cowsPct: null,
  calvesPct: null,
  emissionFactorKgCo2Eq: null,
});

const getEnergyValuesFromFeedType = (feedType: DairyFeedType | undefined) => {
  if (!feedType) {
    return { energyValue: null, energyUnit: null };
  }

  if (feedType.energyMjNelKgDm) {
    return {
      energyValue: feedType.energyMjNelKgDm,
      energyUnit: DAIRY_FEED_ENERGY_UNIT.MJNEL,
    };
  }

  if (feedType.energyMjMeKgDm) {
    return {
      energyValue: feedType.energyMjMeKgDm,
      energyUnit: DAIRY_FEED_ENERGY_UNIT.MJME,
    };
  }

  return { energyValue: null, energyUnit: null };
};

const FeedInformationForm = ({ primaryDataId, readOnly, farmId, year }: DairyFeedTypesFormProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);

  const feedInformationFullQuery = useGetFootprintingDairyFeedInformationByPrimaryDataId(primaryDataId ?? "");
  const { data: feedInformationQuery } = feedInformationFullQuery;
  const dairyFeedTypesFullQuery = useGetDairyFeedTypes();
  const { data: dairyFeedTypesQuery } = dairyFeedTypesFullQuery;
  const { data: primaryData } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { data: herdSize } = useGetFootprintingDairyHerdSize(primaryDataId ?? "");
  const isLoading = [feedInformationFullQuery, dairyFeedTypesFullQuery].some(queryIsLoading);

  const keyedFeedInformations = useKeyedFormList<UploadableFeedInformation>(formRef, { isReady: !isLoading });

  const selectGroupName = "footprinting-dairy-feed-crop-select";

  const { mutate: mutateFeedInformation } = useMutateFootprintingDairyFeedInformation(
    farmId,
    year,
    keyedFeedInformations.payload.filter((f) => !!f.feedTypeId),
    primaryDataId ?? ""
  );

  const markFormPopulated = useSaveFormOnUnmount(mutateFeedInformation, !readOnly && !isLoading, !primaryDataId);

  useEffect(() => {
    if (keyedFeedInformations.entries.length === 0 || keyedFeedInformations.entries.some((f) => !f.id)) return;
    formRef.current?.validateFields();
  }, [keyedFeedInformations.entries]);

  useEffect(() => {
    if (!feedInformationQuery || !dairyFeedTypesQuery) {
      return;
    }

    const newFeedTypes: UploadableFeedInformation[] = feedInformationQuery.map((f) => ({
      id: f.id,
      feedTypeId: f.feedTypeId ?? "",
      isSelfProduced: f.isSelfProduced ?? null,
      totalAmountFedTs: f.totalAmountFedTs ?? null,
      silagePct: f.silagePct ?? null,
      energyValue: f.energyValue ?? null,
      energyUnit: f.energyUnit ?? null,
      dryMatterPct: f.dryMatterPct ?? null,
      cowsPct: f.cowsPct ?? null,
      calvesPct: f.calvesPct ?? null,
      emissionFactorKgCo2Eq: f.emissionFactorKgCo2Eq ?? null,
    }));

    markFormPopulated();

    // Each entry mounts its fields fresh under its own `clientKey`, so `initialValue` seeds the form without a manual sync
    keyedFeedInformations.actions.hydrate(newFeedTypes.length > 0 ? newFeedTypes : [createFeedTypePlaceholder()]);
  }, [feedInformationQuery, dairyFeedTypesQuery, markFormPopulated, keyedFeedInformations.actions]);

  const isAnyFeedTypeSilage = keyedFeedInformations.entries.some((crop) => dairyFeedTypesQuery?.find((f) => f.id === crop.feedTypeId)?.isSilage ?? false);

  const feedValidation = useMemo(() => {
    const totalCowCount = herdSize?.reduce((sum, herd) => sum + herd.cowCount, 0) ?? 0;
    const milkYieldKg = primaryData?.milkYieldKg;

    if (!totalCowCount || !milkYieldKg) {
      return { energyMjNelPerCow: 0, isTooHigh: false, isTooLow: false };
    }

    const validFeeds = keyedFeedInformations.entries.filter(
      (f) =>
        f.totalAmountFedTs !== null &&
        f.energyValue !== null &&
        f.dryMatterPct !== null &&
        f.feedTypeId &&
        dairyFeedTypesQuery?.find((ft) => ft.id === f.feedTypeId && !ft.isSilage)
    );

    if (validFeeds.length === 0) {
      return { energyMjNelPerCow: 0, isTooHigh: false, isTooLow: false };
    }

    const energyCorrected = validFeeds.reduce((totalEnergyCorrected, feed) => {
      const amount = feed.totalAmountFedTs ?? 0;
      const dryMatterFraction = feed.dryMatterPct ?? DEFAULT_DRY_MATTER_PERCENTAGE;
      let energyInNel = feed.energyValue ?? 0;

      // if user enters feedtypes that are mj me convert to mj nel
      if (feed.energyUnit === DAIRY_FEED_ENERGY_UNIT.MJME) {
        const feedType = dairyFeedTypesQuery?.find((ft) => ft.id === feed.feedTypeId);
        if (feedType?.mjMeToMjNel) energyInNel *= feedType.mjMeToMjNel;
      }

      // energyInNel is MJ per kg dry matter; amount is fed as-is, so convert via dryMatterFraction first
      return totalEnergyCorrected + amount * dryMatterFraction * energyInNel;
    }, 0);

    const energyCorrectedPerCow = (energyCorrected * CORRECTED_ENERGY_CONSTANT) / totalCowCount;
    const efficiency = energyCorrectedPerCow / milkYieldKg;

    return {
      energyMjNelPerCow: Math.round(energyCorrectedPerCow * STANDARD_FEED_ENERGY_CONTENT_MJNEL),
      isTooHigh: efficiency > 0.5,
      isTooLow: efficiency < 0.1,
      isInRange: efficiency >= 0.1 && efficiency <= 0.5,
    };
  }, [keyedFeedInformations.entries, primaryData, herdSize, dairyFeedTypesQuery]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Flex vertical gap={16}>
      <Card dataTestId="footprinting-dairy-feed-crops-form">
        <Form ref={formRef} onValuesChange={markFormPopulated}>
          <Flex vertical gap={16}>
            <Flex vertical className="padding-100 padding-left-0" gap={8}>
              <Typography variant="h3">{t("dairyFeedTypesForm.header")}</Typography>
              <Typography variant="paragraph-m" color="grey-600" className="margin-0">
                {t("dairyFeedTypesForm.description")}
              </Typography>
            </Flex>

            {keyedFeedInformations.entries.map((feedInfo, index) => {
              const selectGroupWithId = `${selectGroupName}-${index}`;
              const { clientKey } = feedInfo;
              const updateItem = (changes: Partial<UploadableFeedInformation>) => keyedFeedInformations.actions.update(clientKey, changes);

              const handleFeedDistributionChange = (value: number | null, target: "cows" | "calves") => {
                const pct = formatPercentageInput(value);

                if (pct === null) {
                  updateItem({ ...feedInfo, cowsPct: null, calvesPct: null });
                  formRef.current?.setFieldValue(`cowsPct-${clientKey}`, null);
                  formRef.current?.setFieldValue(`calvesPct-${clientKey}`, null);
                  return;
                }

                if (target === "cows") {
                  updateItem({ ...feedInfo, cowsPct: pct, calvesPct: 1 - pct });
                  formRef.current?.setFieldValue(`cowsPct-${clientKey}`, formatFractionInput(pct));
                  formRef.current?.setFieldValue(`calvesPct-${clientKey}`, formatFractionInput(1 - pct));
                } else {
                  updateItem({ ...feedInfo, calvesPct: pct, cowsPct: 1 - pct });
                  formRef.current?.setFieldValue(`cowsPct-${clientKey}`, formatFractionInput(1 - pct));
                  formRef.current?.setFieldValue(`calvesPct-${clientKey}`, formatFractionInput(pct));
                }
              };

              const selectedFeedType = dairyFeedTypesQuery?.find((ft) => ft.id === feedInfo.feedTypeId);
              const isCustomFeed = selectedFeedType?.isCustom ?? false;
              const isSilage = selectedFeedType?.isSilage ?? false;

              return (
                <Card key={clientKey} className="feed-cards padding-200">
                  <Flex vertical gap={16}>
                    <Flex justify="space-between" align="center" vertical={false}>
                      <Typography variant="h5" style={{ flex: 1 }}>
                        {selectedFeedType
                          ? t("dairyFeedTypesForm.feedTypeCount", {
                              count: index + 1,
                              cropName: selectedFeedType.name,
                            })
                          : t("dairyFeedTypesForm.feedTypeCountNoName", { count: index + 1 })}
                      </Typography>
                      {isCustomFeed && <Tag text={t("dairyFeedTypesForm.question.customFeed")} colour="blue" size="small" />}
                      <IconButton
                        type="tertiary"
                        disabled={readOnly}
                        dataTestId={`delete-feed-crop-${index}`}
                        size="small"
                        iconName="tabler/trash"
                        onClick={() => keyedFeedInformations.actions.remove(clientKey)}
                      />
                    </Flex>

                    <Flex gap={16} align="center">
                      <Form.Item className="flex-1">
                        <Select
                          showSearch
                          disabled={readOnly}
                          name={`${selectGroupName}-${clientKey}`}
                          dataTestId={selectGroupWithId}
                          label={t("dairyFeedTypesForm.question.feedType")}
                          rules={[{ required: true, message: "" }]}
                          initialValue={feedInfo.feedTypeId ?? ""}
                          value={feedInfo.feedTypeId ?? ""}
                          onChange={(selectedFeedId) => {
                            // This .find cannot fail but typescript doesn't know that
                            const feedType = dairyFeedTypesQuery?.find((feedType) => feedType.id === selectedFeedId);
                            const { energyValue, energyUnit } = getEnergyValuesFromFeedType(feedType);

                            // Custom Feed is the one type with no dryMatterPerFeedTypePct behind it. Left blank, the
                            // calculation in peas falls back to 88% without the farmer ever seeing it — and 88% is
                            // roughly three times wrong for a silage. Seeding the field puts the assumption on screen
                            // where it can be corrected instead of being applied silently.
                            const dryMatterPct = feedType?.dryMatterPerFeedTypePct ?? (feedType?.isCustom ? DEFAULT_DRY_MATTER_PERCENTAGE : null);

                            updateItem({
                              ...feedInfo,
                              feedTypeId: feedType?.id ?? "",
                              energyValue,
                              energyUnit: energyUnit ?? DAIRY_FEED_ENERGY_UNIT.MJNEL,
                              silagePct: null,
                              dryMatterPct,
                              totalAmountFedTs: feedType?.isSilage ? 0 : feedInfo.totalAmountFedTs,
                              emissionFactorKgCo2Eq: null,
                            });

                            formRef.current?.setFieldsValue({
                              [`energyValue-${clientKey}`]: energyValue,
                              [`dryMatterPct-${clientKey}`]: formatFractionInput(dryMatterPct),
                              [`silagePct-${clientKey}`]: null,
                              [`totalAmountFed-${clientKey}`]: feedType?.isSilage ? 0 : feedInfo.totalAmountFedTs,
                              [`emissionFactorKgCo2Eq-${clientKey}`]: null,
                            });
                          }}
                          dropdownStyle={{ zIndex: 10010 }}
                        >
                          {(dairyFeedTypesQuery ?? []).map((feedType) => {
                            // Check if this feed type ID is already used in another item (Custom Feed is exempt — multiple rows are allowed)
                            const isAlreadyUsed =
                              !feedType.isCustom &&
                              keyedFeedInformations.entries.some((f) => f.feedTypeId === feedType.id && f.feedTypeId !== feedInfo.feedTypeId);

                            return (
                              <Option
                                disabled={readOnly || isAlreadyUsed}
                                key={`${selectGroupWithId}-${feedType.id}`}
                                data-testid={`${selectGroupWithId}-${feedType.id}`}
                                value={feedType.id}
                              >
                                {feedType.name}
                              </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>

                      <Flex vertical align="center" gap={8}>
                        <Typography variant="paragraph-s" className="margin-0">
                          {t("dairyFeedTypesForm.question.isSelfProduced")}
                        </Typography>
                        <Switch
                          disabled={readOnly}
                          // dataTestId={`footprinting-dairy-feed-is-selfProduced-${index}`}
                          value={feedInfo.isSelfProduced ?? false}
                          onChange={(v) => updateItem({ ...feedInfo, isSelfProduced: v })}
                        />
                      </Flex>
                    </Flex>

                    {isSilage ? (
                      <InputNumber
                        disabled={readOnly}
                        dataTestId={`footprinting-dairy-feed-silage-pct-${index}`}
                        name={`silagePct-${clientKey}`}
                        label={t("dairyFeedTypesForm.question.silagePct")}
                        suffix="%"
                        value={formatFractionInput(feedInfo.silagePct ?? null)}
                        initialValue={formatFractionInput(feedInfo.silagePct ?? null)}
                        onChange={(v) => {
                          updateItem({ ...feedInfo, silagePct: formatPercentageInput(v) });
                          setTimeout(() => {
                            keyedFeedInformations.entries.forEach((f) => {
                              if (dairyFeedTypesQuery?.find((ft) => ft.id === f.feedTypeId)?.isSilage) {
                                formRef.current?.validateFields([`silagePct-${f.clientKey}`]);
                              }
                            });
                          }, 0);
                        }}
                        precision={2}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.validPercentage")));
                              }
                              const total = keyedFeedInformations.entries.reduce((sum, f, i) => {
                                const value = i === index ? formatPercentageInput(v) ?? 0 : f.silagePct ?? 0;
                                return sum + value;
                              }, 0);
                              if (Math.round(total * 100) > 100) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.combinedSilagePercentage")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    ) : (
                      <InputNumber
                        disabled={readOnly}
                        dataTestId={`footprinting-dairy-feed-amount-input-${index}`}
                        name={`totalAmountFed-${clientKey}`}
                        label={t("dairyFeedTypesForm.question.totalAmountFed")}
                        suffix={t("dairyFeedTypesForm.question.totalAmountFedUnit")}
                        value={feedInfo.totalAmountFedTs}
                        initialValue={feedInfo.totalAmountFedTs}
                        onChange={(v) => updateItem({ ...feedInfo, totalAmountFedTs: v })}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPositiveNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.validAmount")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    )}

                    <Typography variant="h6">{t("dairyFeedTypesForm.basicInformation")}</Typography>

                    <Form.Item className={isCustomFeed ? "input-inline-container" : ""}>
                      <InputNumberWithUnitSelector
                        disabled={readOnly}
                        dataTestId={`footprinting-dairy-feed-energy-input-${index}`}
                        name={`energyValue-${clientKey}`}
                        label={t("dairyFeedTypesForm.question.energyValue")}
                        unitLabel={t("unit", { ns: "footprinting" })}
                        // Custom Feed has no catalog ME→NEL conversion factor, so it's locked to NEL
                        // (the unit PEAS expects) instead of offering a selector.
                        // Locked to NEL only (no selector) per Christian's decision on PE-937 — keeps
                        // custom feed effort low and avoids the unreliable ME↔NEL conversion.
                        units={isCustomFeed ? undefined : Object.values(DAIRY_FEED_ENERGY_UNIT)}
                        value={feedInfo.energyValue}
                        initialValue={feedInfo.energyValue}
                        unitValue={feedInfo.energyUnit}
                        onChange={(v, unit) => updateItem({ ...feedInfo, energyValue: v, energyUnit: unit as ENUM_DAIRY_FEED_ENERGY_UNIT })}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPositiveNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.validNumber")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                      {/* Custom Feed has no unit selector (locked to NEL), so surface the unit as a static suffix instead. */}
                      {isCustomFeed && (
                        <Typography variant="paragraph-m" className="klim-suffix">
                          {getUnitDisplayLabel(DAIRY_FEED_ENERGY_UNIT.MJNEL)}
                        </Typography>
                      )}
                    </Form.Item>

                    <InputNumber
                      disabled={readOnly}
                      dataTestId={`footprinting-dairy-feed-drymatter-input-${index}`}
                      name={`dryMatterPct-${clientKey}`}
                      label={t("dairyFeedTypesForm.question.dryMatterPct")}
                      suffix="%"
                      value={formatFractionInput(feedInfo.dryMatterPct ?? null)}
                      initialValue={formatFractionInput(feedInfo.dryMatterPct ?? null)}
                      onChange={(v) => updateItem({ ...feedInfo, dryMatterPct: formatPercentageInput(v) })}
                      precision={2}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("dairyFeedTypesForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />

                    {isCustomFeed && (
                      <InputNumber
                        disabled={readOnly}
                        dataTestId={`footprinting-dairy-feed-emission-factor-input-${index}`}
                        name={`emissionFactorKgCo2Eq-${clientKey}`}
                        label={t("dairyFeedTypesForm.question.emissionFactor")}
                        tooltip={t("dairyFeedTypesForm.tooltip.emissionFactor")}
                        suffix={t("dairyFeedTypesForm.question.emissionFactorUnit")}
                        value={feedInfo.emissionFactorKgCo2Eq}
                        initialValue={feedInfo.emissionFactorKgCo2Eq}
                        onChange={(v) => updateItem({ ...feedInfo, emissionFactorKgCo2Eq: v })}
                        precision={4}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedTypesForm.error.validEmissionFactor")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    )}

                    <Flex vertical>
                      <Typography variant="h6">{t("dairyFeedTypesForm.feedDistribution")}</Typography>
                      <Typography variant="paragraph-m" color="grey-600" className="margin-0">
                        {t("dairyFeedTypesForm.feedDistributionDescription")}
                      </Typography>
                    </Flex>

                    <Form.Item>
                      <InputImageCard
                        disabled={readOnly}
                        title={t("dairyFeedTypesForm.question.cowsPct")}
                        tooltip={t("dairyFeedTypesForm.tooltip.cowsPct")}
                        suffix="%"
                        name={`cowsPct-${clientKey}`}
                        value={formatFractionInput(feedInfo.cowsPct ?? null)}
                        initialValue={formatFractionInput(feedInfo.cowsPct ?? null)}
                        onChange={(v) => {
                          handleFeedDistributionChange(v, "cows");
                          setTimeout(() => {
                            keyedFeedInformations.entries.forEach((f) => {
                              formRef.current?.validateFields([`cowsPct-${f.clientKey}`, `calvesPct-${f.clientKey}`]);
                            });
                          }, 0);
                        }}
                        precision={2}
                        image={{
                          imageAlt: "Cow image",
                          imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/mixed_breed_dairy.png",
                        }}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject();
                              }

                              if (v / 100 + (feedInfo.calvesPct ?? 0) > 1) {
                                return Promise.reject();
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item>
                      <InputImageCard
                        disabled={readOnly}
                        title={t("dairyFeedTypesForm.question.calvesPct")}
                        tooltip={t("dairyFeedTypesForm.tooltip.calvesPct")}
                        suffix="%"
                        name={`calvesPct-${clientKey}`}
                        value={formatFractionInput(feedInfo.calvesPct ?? null)}
                        initialValue={formatFractionInput(feedInfo.calvesPct ?? null)}
                        onChange={(v) => {
                          handleFeedDistributionChange(v, "calves");
                          setTimeout(() => {
                            keyedFeedInformations.entries.forEach((f) => {
                              formRef.current?.validateFields([`cowsPct-${f.clientKey}`, `calvesPct-${f.clientKey}`]);
                            });
                          }, 0);
                        }}
                        precision={2}
                        image={{
                          imageSize: 48,
                          imageAlt: "Calves image",
                          imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/calve.png",
                        }}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject();
                              }

                              if (v / 100 + (feedInfo.cowsPct ?? 0) > 1) {
                                return Promise.reject();
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>

                    {feedInfo.cowsPct === null ||
                    feedInfo.calvesPct === null ||
                    isPercentageComplete((feedInfo.cowsPct ?? 0) + (feedInfo.calvesPct ?? 0)) ? null : (
                      <AlertBanner
                        type="error"
                        iconName="tabler/alert-triangle"
                        iconAlign="start"
                        iconSize={24}
                        gap={4}
                        headingSize="h6"
                        description={t("dairyFeedTypesForm.error.invalidDistributionDescription")}
                        title={
                          (feedInfo.cowsPct ?? 0) + (feedInfo.calvesPct ?? 0) > 1
                            ? t("dairyFeedTypesForm.error.invalidDistributionTitleTooHigh")
                            : t("dairyFeedTypesForm.error.invalidDistributionTitleTooLow")
                        }
                      />
                    )}
                  </Flex>
                </Card>
              );
            })}
            {isAnyFeedTypeSilage && (
              <ProgressBarCardWithContent
                currentValue={keyedFeedInformations.entries.filter((fi) => fi.feedTypeId).reduce((sum, crop) => sum + (crop.silagePct ?? 0) * 100, 0)}
                title={t("dairyFeedTypesForm.silagePercentageSum.title")}
                showPercentValue={true}
                showRemainingPercentage={true}
                onAnimationFinish={() => {}}
                errorMessage={t("dairyFeedTypesForm.silagePercentageSum.errorMessage")}
                successMessage={t("dairyFeedTypesForm.silagePercentageSum.successMessage")}
                infoMessage={t("dairyFeedTypesForm.silagePercentageSum.description")}
              />
            )}
            <Button
              type="text"
              dataTestId="add-feed-crop-button"
              disabled={readOnly}
              onClick={() => {
                keyedFeedInformations.actions.add(createFeedTypePlaceholder());
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("dairyFeedTypesForm.addFeedType")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card border dataTestId="footprinting-dairy-feed-energy-check-card" className="padding-200">
        <Flex vertical gap={16}>
          <Flex vertical gap={8}>
            <Typography variant="h3">{t("dairyFeedTypesForm.feedEnergyTitle")}</Typography>
            <Typography variant="paragraph-m" color="grey-600">
              {t("dairyFeedTypesForm.feedEnergyDescription")}
            </Typography>
          </Flex>

          <Card border className="dairy-display-information-card padding-100 flex-center">
            <Flex vertical>
              <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-25 center">
                {t("dairyFeedTypesForm.feedEnergyDisplayTitle")}
              </Typography>
              <Typography variant="h5" className="center">
                {feedValidation.energyMjNelPerCow} MJ NEL / {t("dairyFeedTypesForm.feedEnergyDisplayCowUnit")}
              </Typography>
              {/* <p className="paragraph-xs grey-600-text margin-0 margin-bottom-25 margin-top-25">{t("dairyFeedTypesForm.feedEnergyDisplayRange")}</p> */}
            </Flex>
          </Card>

          {(feedValidation.isTooHigh || feedValidation.isTooLow || feedValidation.isInRange) && (
            <AlertBanner
              type={feedValidation.isInRange ? "success" : "warning"}
              iconName={feedValidation.isInRange ? "tabler/confetti" : "tabler/info-circle"}
              iconAlign="start"
              iconSize={24}
              headingSize="h6"
              title={
                feedValidation.isTooHigh
                  ? t("dairyFeedTypesForm.error.feedAmountTooHigh")
                  : feedValidation.isTooLow
                  ? t("dairyFeedTypesForm.error.feedAmountTooLow")
                  : t("dairyFeedTypesForm.success.feedAmountInRange")
              }
              description={
                feedValidation.isTooHigh
                  ? t("dairyFeedTypesForm.error.feedValidationDescriptionHigh")
                  : feedValidation.isTooLow
                  ? t("dairyFeedTypesForm.error.feedValidationDescriptionLow")
                  : t("dairyFeedTypesForm.success.feedValidationDescriptionInRange")
              }
            />
          )}
        </Flex>
      </Card>
    </Flex>
  );
};

FeedInformationForm.Key = "dairy.feedInformation";
export { FeedInformationForm };
